import {t} from 'i18next'
import {Button} from 'primereact/button'
import {Column} from 'primereact/column'
import {DataTable} from 'primereact/datatable'
import {Toast} from 'primereact/toast'
import React, {useEffect, useRef, useState} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {useLocation, useNavigate} from 'react-router-dom'
import {AddGroupMemberForm} from './AddGroupMember'
import {GroupForm} from './GroupForm'
import {claims} from '../../../../claims'
import {API_GROUP_BY_ID, API_GROUP_MEMBERS_BY_GROUP_ID_AND_MEMBER_ID} from '../../../../constants'
import {RequestMethod} from '../../../../model/enums/enums'
import {Group, GroupMember} from '../../../../model/group.model'
import {removeGroupMember, updateGroup} from '../../../../redux/basic/group/groupActions'
import {useAppDispatch, useAppSelector} from '../../../../redux/hooks'
import {AuthRoutes} from '../../../../router/routes'
import {handleRequest} from '../../../../services/api/base/handleRequest'
import {findPersonById} from '../../../../util/arraySearch'
import {hasAnyRequiredClaim, hasRequiredClaim} from '../../../../util/validation/hasClaim'
import {BackButton} from '../../../util/button/BackButton'
import {actionBodyDelete} from '../../../util/data-table/cell-body/ActionBody'
import {AvatarCell} from '../../../util/data-table/cell-body/FullNameApprenticeBody'

// default group so that we are able
// to correctly load the data
// into the form from the state
const defaultGroup: Group = {
  group_name: '',
  group_description: '',
  is_active: 1,
  group_id: 0,
  last_change: undefined,
}

export const GroupEntry = (): JSX.Element => {
  const groups = useAppSelector((state) => state.groups)
  const { loadedBasicInformation } = useAppSelector((state) => state.user)

  const userClaims = useAppSelector((state) => state.claims)

  const persons = useAppSelector((state) => [
    ...state.apprentices,
    ...state.employees,
  ])

  const [gID, setGID] = useState<number | undefined>()
  const dispatcher = useAppDispatch()

  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldFocusError: true,
    defaultValues: defaultGroup,
  })

  const toast = useRef<Toast>(null)

  const selectedGroup = useAppSelector((state) =>
    state.groups.find((group: Group) => group.group_id === gID)
  )

  const location = useLocation()
  const navigate = useNavigate()

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)

  // subscribe to set the buttons to disabled
  // if nothing has changed
  useEffect(() => {
    const subscription = methods.watch(async (value, { name, type }) => {
      if (type === 'change' || type === undefined) {
        setHasUnsavedChanges(true)
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  // this effect parses the url to load the correct group
  useEffect(() => {
    const substrings = location.pathname.split('/')
    const lastValue = substrings[substrings.length - 1]

    const groupId = parseInt(lastValue)

    if (!groupId) {
      console.warn(
        '[EditGroup] GroupID is not a valid number. Something went wrong.'
      )
      navigate(AuthRoutes.groups)
    }

    setGID(groupId)

    if (
      loadedBasicInformation &&
      !groups.find((group: Group) => groupId === group.group_id)
    ) {
      console.warn(
        '[EditGroup] GroupID is not a valid group ID. Something went wrong.'
      )
      navigate(AuthRoutes.groups)
    }
  }, [location, loadedBasicInformation])

  // this effect is necessary so that the correct group can be loaded after parsing
  useEffect(() => {
    // we need an additional check here so that it really only loads once
    // and that it does not reset the form on deletion and addition
    // where the selected group is also triggered
    if (
      loadedBasicInformation &&
      selectedGroup &&
      methods.getValues('group_id') === 0
    ) {
      methods.reset(selectedGroup)
      setHasUnsavedChanges(false)
    }
  }, [selectedGroup, loadedBasicInformation])

  const onSubmit = async (formData: Group): Promise<void> => {
    // make sure that no wrong format is sent to backend
    formData.last_change = undefined
    const result = await handleRequest<Group>(
      API_GROUP_BY_ID(formData.group_id),
      RequestMethod.PUT,
      formData
    )

    if (result.error || !result.data) {
      console.warn(
        '[Group Entry] An error occurred when changing group name or description'
      )
      return
    }

    // we need to add the module plans since they are not included in the answer to the post
    const newFormData = { ...result.data }
    newFormData.group_apprenticeship_module_plans =
      formData.group_apprenticeship_module_plans
    dispatcher(updateGroup(newFormData))
    methods.reset(newFormData)
    setHasUnsavedChanges(false)

    toast.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.save.success.title'),
      detail: t<string>('core.action.save.success.message'),
      life: 4000,
    })
  }

  const personBody = (groupMember: GroupMember): JSX.Element => {
    const person = findPersonById(groupMember?.id_person, persons)
    return AvatarCell(person)
  }

  const removeMember = async (formData: GroupMember) => {
    const result = await handleRequest(
      API_GROUP_MEMBERS_BY_GROUP_ID_AND_MEMBER_ID(
        formData.id_group,
        formData.group_member_id
      ),
      RequestMethod.DELETE
    )

    if (result.error) {
      console.warn(
        '[Group Entry]: An error occurred when trying to delete a member'
      )
      return
    }

    dispatcher(removeGroupMember(formData.id_group, formData.group_member_id))
    toast.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.delete.confirmed.title'),
      detail: t<string>('core.action.delete.confirmed.message'),
      life: 12000,
    })
  }

  return (
    <div className={'pr-2'}>
      {selectedGroup && (
        <h2 className="pt-2">
          {`${t<string>('hrm.group.name')}: ${selectedGroup?.group_name} (ID_${
            selectedGroup?.group_id
          })`}
        </h2>
      )}
      <BackButton
        translationKey={'navigation.go_group_overview'}
        route={AuthRoutes.groups}
      />

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <b>{t<string>('hrm.group.name')}</b>
          <div className={'formgrid grid flex-wrap p-fluid ml-0 mt-1'}>
            <GroupForm
              disabled={!hasRequiredClaim(userClaims, claims.hrm.groups.update)}
            />
            <div className={'col-12 md:col-2 flex align-items-center pl-2'}>
              {hasRequiredClaim(userClaims, claims.hrm.groups.update) && (
                <Button
                  type={'submit'}
                  label={t<string>('hrm.group.edit_group')}
                  disabled={!hasUnsavedChanges}
                />
              )}
            </div>
          </div>
        </form>
      </FormProvider>

      <div className={'mt-2'}>
        {hasAnyRequiredClaim(userClaims, [
          claims.hrm.groups.createMember,
          claims.hrm.groups.updateMember,
        ]) && (
          <AddGroupMemberForm
            toastRef={toast}
            group_id={selectedGroup?.group_id || 0}
          ></AddGroupMemberForm>
        )}
      </div>

      <div style={{ height: 'calc(100vh - 400px)', overflow: 'auto' }}>
        <DataTable
          value={selectedGroup?.group_member}
          header={t<string>('hrm.group.members')}
          scrollHeight={'flex'}
          scrollable
          className={'p-datatable pt-4'}
          rowHover
          stripedRows
          selectionMode="single"
          responsiveLayout="scroll"
          emptyMessage={t<string>('core.empty_message')}
        >
          <Column
            header={t<string>('hrm.group.member.name')}
            field={'id_person'}
            body={personBody}
          />
          <Column
            header={t<string>('hrm.group.member.role_name')}
            field={'role_name'}
            sortable
            dataType={'text'}
          />

          {hasRequiredClaim(userClaims, claims.hrm.groups.deleteMember) && (
            <Column
              body={actionBodyDelete(removeMember)}
              style={{ maxWidth: '80px' }}
            />
          )}
        </DataTable>
      </div>
      <Toast className={' pt-2 mt-2'} ref={toast} position="bottom-right" />
    </div>
  )
}